<template>
  <ListingTemplate :customClass="'export-listing'">
    <template v-slot:body>
      <ListingHeader>
        <template v-slot:content>
          <v-col cols="12" class="pt-0 d-flex justify-flex-end export-filters">
            <v-text-field
              v-model.trim="listingSearch.search"
              clearable
              dense
              filled
              flat
              hide-details
              label="Search"
              solo
              :disabled="dataLoading"
              color="cyan"
              append-icon="mdi-magnify"
              class="export-search-field"
              @click:clear="clearSearch"
              @keydown.enter="filterRows"
            ></v-text-field>
            <v-menu
              v-model="datePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  dense
                  filled
                  flat
                  hide-details
                  label="Date Range"
                  solo
                  readonly
                  clearable
                  prepend-inner-icon="mdi-calendar"
                  :value="formattedDate"
                  :disabled="dataLoading"
                  color="cyan"
                  class="ml-2 export-date-field"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearFilter('dates')"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dates"
                range
                color="cyan"
                :disabled="dataLoading"
                @change="applyDateFilter"
              ></v-date-picker>
            </v-menu>
            <v-menu
              max-height="400"
              max-width="250"
              left
              offset-y
              :close-on-content-click="false"
              content-class="white-background"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  depressed
                  class="ml-2 custom-bold-button btn-tab-hide"
                  color="cyan white--text"
                  :disabled="dataLoading"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon dark>mdi-table-edit</v-icon>
                </v-btn>
              </template>
              <draggable
                tag="ul"
                v-model="defaultColDefs"
                class="draggable-group"
                handle=".draggable-drag-icon"
                @change="updateVisible"
              >
                <li
                  v-for="column in configurableColumns"
                  :key="column.field"
                  class="draggable-group-item"
                >
                  <v-checkbox
                    v-model="defaultColShow"
                    dense
                    hide-details
                    class="mt-0 mb-0"
                    :value="column.field"
                    :label="column.headerName"
                    :disabled="Number(column.fixed) > 0 || dataLoading"
                    color="cyan"
                    @change="updateVisible"
                  ></v-checkbox>
                  <v-icon class="draggable-drag-icon" right color="cyan"
                    >mdi-drag</v-icon
                  >
                </li>
              </draggable>
            </v-menu>
          </v-col>
        </template>
      </ListingHeader>

      <ListingSearchCriteria
        v-if="searchEnabled"
        pageModule="export-listing"
        :basicSearchFields="['module', 'barcode', 'status']"
        :allowedSearchFields="searchableArray"
      >
      </ListingSearchCriteria>

      <perfect-scrollbar
        :options="{ suppressScrollX: true }"
        class="scroll custom-box-top-inner-shadow"
        style="max-height: 90vh; position: relative"
      >
        <ListingTable
          :columnCount="columnLength() || 1"
          :dataLoading="dataLoading"
          :rowData="rowData"
        >
          <template v-slot:thead>
            <thead>
              <tr>
                <template v-for="cols in customThead">
                  <th
                    v-if="cols.visible && cols.field !== 'id'"
                    :key="cols.id || cols.field"
                    class="simple-table-th"
                    :class="{ 'text-center': cols.field === 'status' }"
                  >
                    <div
                      v-if="cols.sortable"
                      class="simple-table-text sortable"
                      @click="
                        !dataLoading &&
                          updateSorting({ field: cols.field, sort: cols.sort })
                      "
                    >
                      {{ cols.headerName }}
                    </div>
                    <div v-else class="simple-table-text">
                      {{ cols.headerName }}
                    </div>
                    <v-icon v-if="cols.sort === 'asc'" small color="cyan"
                      >mdi-sort-ascending</v-icon
                    >
                    <v-icon v-if="cols.sort === 'desc'" small color="cyan"
                      >mdi-sort-descending</v-icon
                    >
                  </th>
                </template>
              </tr>
            </thead>
          </template>
          <template v-slot:tbody>
            <tbody class="custom-border-bottom">
              <tr
                v-for="(data, index) in rowData"
                :key="index"
                class="service-listing-row"
              >
                <template v-for="cols in customThead">
                  <td
                    v-if="cols.visible && cols.field !== 'id'"
                    :key="cols.id || cols.field"
                    class="simple-table-td"
                  >
                    <!-- Module Name & Trigger Event -->
                    <template v-if="cols.field === 'module'">
                      <div class="mt-0 mb-1 d-flex flex-column align-start">
                        <v-chip
                          small
                          class="text-capitalize"
                          color="blue"
                          label
                          outlined
                        >
                          {{ data.module || "--" }}
                        </v-chip>
                        <v-chip
                          v-if="data.event_text || data.event || data.action"
                          x-small
                          label
                          :color="eventBgColor(data.event || data.action)"
                          class="mt-1 font-weight-600 px-2"
                          :class="`${eventColor(data.event || data.action)}--text`"
                        >
                          <v-icon x-small left :color="eventColor(data.event || data.action)">mdi-lightning-bolt</v-icon>
                          {{ data.event_text || formatEventName(data.event || data.action) }}
                        </v-chip>
                      </div>
                    </template>

                    <!-- Barcode / Reference -->
                    <template v-else-if="cols.field === 'entity_barcode'">
                      <div class="d-flex align-center">
                        <Barcode
                          v-if="data.entity_barcode"
                          :barcode="data.entity_barcode"
                        ></Barcode>
                        <span v-else class="text-muted">--</span>

                        <v-tooltip
                          top
                          max-width="450"
                          content-class="custom-top-tooltip"
                          v-if="(data.status === 'success' || data.status === 'synced') && data.ignored_items && data.ignored_items.length > 0"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              x-small
                              color="amber lighten-5"
                              class="amber--text text--darken-4 font-weight-600 ml-2 px-1 cursor-pointer"
                              v-bind="attrs"
                              v-on="on"
                              label
                            >
                              <v-icon x-small left color="amber darken-3">mdi-filter-remove-outline</v-icon>
                              {{ data.ignored_items.length }} Ignored
                            </v-chip>
                          </template>
                          <div class="p-1">
                            <div class="font-weight-bold text-warning mb-1">
                              Items Ignored from Info-Tech Sync:
                            </div>
                            <div
                              v-for="(it, idx) in data.ignored_items"
                              :key="idx"
                              class="font-size-12 line-height-normal my-1"
                            >
                              • <strong>[{{ it.type }}]</strong> {{ it.title }}
                              <template v-if="it.quantity">(Qty: {{ it.quantity }}</template>
                              <template v-if="it.rate">, Rate: {{ formatMoney(it.rate) }})</template>
                              <template v-else-if="it.quantity">)</template>
                            </div>
                          </div>
                        </v-tooltip>
                      </div>
                    </template>

                    <!-- Remote ID -->
                    <template v-else-if="cols.field === 'infotech_id'">
                      <v-chip
                        v-if="data.infotech_id"
                        small
                        label
                        color="cyan lighten-5"
                        text-color="cyan darken-3"
                        class="font-weight-600"
                      >
                        #{{ data.infotech_id }}
                      </v-chip>
                      <span v-else class="text-muted">--</span>
                    </template>

                    <!-- Status -->
                    <template v-else-if="cols.field === 'status'">
                      <div class="d-flex justify-center">
                        <v-chip
                          small
                          label
                          text-color="white"
                          :color="statusColor(data.status)"
                        >
                          {{ formatStatus(data.status) }}
                        </v-chip>
                      </div>
                    </template>

                    <!-- Reason / Message -->
                    <template v-else-if="cols.field === 'reason'">
                      <v-tooltip
                        top
                        max-width="450"
                        content-class="custom-top-tooltip"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <p
                            v-bind="attrs"
                            v-on="on"
                            class="m-0 custom-nowrap-ellipsis max-width-300px"
                          >
                            {{
                              data.reason ||
                              (data.status === "success"
                                ? "Synchronized successfully"
                                : "--")
                            }}
                          </p>
                        </template>
                        <span>
                          {{
                            data.reason ||
                            (data.status === "success"
                              ? "Synchronized successfully"
                              : "--")
                          }}
                        </span>
                      </v-tooltip>
                    </template>

                    <!-- Created Time: Cyan rounded chip matching screenshot -->
                    <template v-else-if="cols.field === 'created_at'">
                      <v-chip
                        small
                        label
                        color="cyan darken-1"
                        text-color="white"
                        class="font-weight-600"
                      >
                        {{ formatDisplayDate(data.created_at) }}
                      </v-chip>
                    </template>

                    <!-- Default / Fallback -->
                    <template v-else>
                      <div v-html="printRowData(cols, data)"></div>
                    </template>
                  </td>
                </template>
              </tr>
              <tr v-if="lodash.isEmpty(rowData) && !dataLoading">
                <td :colspan="columnLength() || 1" class="py-4">
                  <p
                    class="m-0 row-not-found text-center font-weight-500 font-size-16"
                  >
                    No Info-Tech sync logs found.
                  </p>
                </td>
              </tr>
            </tbody>
          </template>
        </ListingTable>
      </perfect-scrollbar>

      <ListingFooter
        :dataLoading="dataLoading"
        :showingFrom="showingFrom"
        :showingTo="showingTo"
        :totalRows="totalRows"
        :currentPage="currentPage"
        :totalPages="totalPages"
      ></ListingFooter>
    </template>
  </ListingTemplate>
</template>

<script>
import draggable from "vuedraggable";
import CommonMixin from "@/core/plugins/common-mixin";
import ListingMixin from "@/core/plugins/listing-mixin";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ListingTemplate from "@/view/pages/partials/Listing-Template.vue";
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import ListingHeader from "@/view/pages/partials/Listing-Header.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import ListingSearchCriteria from "@/view/pages/partials/Listing-Search-Criteria.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import moment from "moment-timezone";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);

export default {
  name: "infotech-logs",
  mixins: [CommonMixin, ListingMixin],
  components: {
    draggable,
    ListingTemplate,
    ListingTable,
    ListingHeader,
    ListingFooter,
    ListingSearchCriteria,
    Barcode,
  },
  data() {
    return {
      pageModule: "infotech-listing",
      routeAPI: "infotech/logs",
      routeName: "infotech.logs",
      status: "all",
      customThead: [],
      datePicker: false,
      searchableArray: ["search", "status", "module", "dates"],
    };
  },
  computed: {
    configurableColumns() {
      return this.defaultColDefs.filter(
        (column) =>
          column.field !== "id" &&
          (!column.checkbox || Number(column.checkbox) === 0)
      );
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Info-Tech Logs" }]);
  },
  beforeMount() {
    this.lodash.assign(this.queryParams, this.$route.query);
    this.status = this.$route.query.status || this.status;
    this.paginationPageSize =
      window.localStorage.getItem(this.pageModule) || 50;
  },
  methods: {
    columnLength() {
      return (
        this.customThead.filter((col) => col.visible && col.field !== "id")
          .length || 6
      );
    },
    formatStatus(status) {
      if (!status) return "--";
      if (status === "success" || status === "synced") return "Success";
      if (status === "failed" || status === "error") return "Failed";
      if (status === "pending" || status === "in_progress") return "Pending";
      if (status === "skipped") return "Skipped";
      return this.lodash.capitalize(status);
    },
    statusColor(status) {
      if (status === "success" || status === "synced") return "green";
      if (status === "failed" || status === "error") return "red";
      if (status === "pending" || status === "in_progress") return "orange";
      if (status === "skipped") return "amber darken-2";
      return "grey";
    },
    eventColor(event) {
      if (!event) return "blue-grey darken-1";
      const ev = String(event).toLowerCase();
      if (ev.includes("create")) return "teal darken-2";
      if (ev.includes("update") && !ev.includes("status")) return "indigo darken-2";
      if (ev.includes("status")) return "deep-purple darken-2";
      if (ev.includes("manual")) return "blue darken-3";
      if (ev.includes("bulk")) return "purple darken-2";
      if (ev.includes("payment")) return "green darken-3";
      if (ev.includes("credit")) return "orange darken-3";
      return "blue-grey darken-2";
    },
    eventBgColor(event) {
      if (!event) return "blue-grey lighten-5";
      const ev = String(event).toLowerCase();
      if (ev.includes("create")) return "teal lighten-5";
      if (ev.includes("update") && !ev.includes("status")) return "indigo lighten-5";
      if (ev.includes("status")) return "deep-purple lighten-5";
      if (ev.includes("manual")) return "blue lighten-5";
      if (ev.includes("bulk")) return "purple lighten-5";
      if (ev.includes("payment")) return "green lighten-5";
      if (ev.includes("credit")) return "orange lighten-5";
      return "blue-grey lighten-5";
    },
    formatEventName(action) {
      if (!action) return "Auto Sync";
      return action
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatDisplayDate(date) {
      if (!date) return "--";
      const m = moment(date);
      if (!m.isValid()) return date;
      return m.format("DD/MM/YYYY HH:mm");
    },
    applyDateFilter() {
      this.currentPage = 1;
      this.getRows();
    },
    clearFilter(field) {
      if (field === "dates") {
        this.dates = [];
        this.currentPage = 1;
        this.getRows();
      }
    },
    updateVisible() {
      for (let index = 0; index < this.defaultColDefs.length; index++) {
        const element = this.defaultColDefs[index];
        this.defaultColDefs[index].visible = 0;
        if (this.defaultColShow.includes(element.field)) {
          this.defaultColDefs[index].visible = 1;
        }
      }
      this.customThead = this.defaultColDefs.filter((col) => col.visible == 1);
    },
  },
};
</script>

<style scoped>
.export-filters {
  gap: 8px;
}

.export-search-field {
  flex: 0 1 320px;
}

.export-date-field {
  flex: 0 1 260px;
}
</style>
